import { useMemo } from 'react';

import { isBoardStatus, type BoardStatus } from '@/pages/Crew/crewRoster';
import type { MappedPersonForSelection } from '@/store/persons/person.dto';
import { mapPeopleToCrewMembers } from '@/store/persons/person.mapper';
import { usePerson } from '@/store/persons/person.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { normalizeRole, SYSTEM_ROLES } from '@/utils/roles';

import type { CrewRosterEntry } from '../types';

/** One name from Crew Management, with where they stand aboard. */
export type LogCrewMember = {
  id: string;
  firstName: string;
  lastName: string;
  avatar: string;
  /** Their role on this vessel, as the crew record holds it. */
  role: string;
  /**
   * On board or on leave — the only two the roster knows, the same two Crew
   * Management offers. A member whose presence was never set, or who still
   * carries the retired `off_board`, reads as on board: they are on the vessel's
   * crew, and a row on today's log has to say one thing or the other.
   */
  presence: BoardStatus;
};

/**
 * **Who is aboard, according to Crew Management — the names a new entry starts
 * with.**
 *
 * Read from the people list the log already loads rather than from the crew
 * page's own `useCrewRoster`: `GET /crew`, `/past-crew` and `/invites/vessel`
 * all sit behind the `CrewManagement` right, so a deckhand opening the daily log
 * would have got three 403s and an empty roster — and the roster is exactly what
 * they are meant to be able to read. `GET /people` needs no right beyond being
 * signed in, and it carries `services[].presence`, which is the whole of what
 * this needs. It is also the read the dashboard's who's-on-board card lives on.
 *
 * Crew Management *seeds* an entry and is never written back to: presence is
 * where someone stands now, and a log entry is where they stood on its day. So
 * the status the roster starts each new entry with is read from the crew record,
 * and every change after that belongs to the entry — saved in its own `duty`
 * field, which is why correcting last Tuesday's log cannot move anyone today.
 */
export const crewMemberToRosterEntry = (member: LogCrewMember): CrewRosterEntry => ({
  userId: member.id,
  firstName: member.firstName,
  lastName: member.lastName,
  avatar: member.avatar,
  roleName: member.role,
  status: member.presence,
  dayOff: false,
  hol: false,
});

export function useLogCrew() {
  const people = usePerson((s) => s.people);
  const vesselId = useVessels((s) => s.vessel?.id) ?? null;

  /**
   * The vessel's active crew, as Crew Management counts it — `mapPeopleToCrewMembers`
   * drops the owner, guests and service providers, so the log's roster is the
   * crew and no one else.
   */
  const crew = useMemo<LogCrewMember[]>(
    () =>
      mapPeopleToCrewMembers(people ?? [], vesselId).map((member) => {
        const service = member.services.find((s) => s.service_id === vesselId);
        const presence = service?.presence;
        return {
          id: member.id,
          firstName: member.firstName,
          lastName: member.lastName ?? '',
          avatar: member.avatar ?? '',
          role: service?.role ?? '',
          presence: isBoardStatus(presence) ? presence : 'on_board',
        };
      }),
    [people, vesselId],
  );

  const crewById = useMemo(() => new Map(crew.map((member) => [member.id, member])), [crew]);

  /**
   * Everyone on this vessel who is *not* crew — the owner, guests and vendors.
   *
   * The log's Add-crew picker used to offer everyone but guests, so an entry
   * saved before this could name the owner or a service provider. They are
   * dropped when such an entry loads, rather than left on a roster that is
   * meant to be the crew and no one else.
   */
  const nonCrewIds = useMemo(() => {
    const ids = new Set<string>();
    for (const person of people ?? []) {
      const service = person.services?.find((s) => s.service_id === vesselId);
      if (service && SYSTEM_ROLES.has(service.role)) ids.add(person.id);
    }
    return ids;
  }, [people, vesselId]);

  /** The Add-crew picker's list — the same active crew, in the picker's shape. */
  const crewForPicker = useMemo<MappedPersonForSelection[]>(
    () =>
      crew.map((member) => ({
        id: member.id,
        firstName: member.firstName,
        lastName: member.lastName,
        avatar: member.avatar,
        role: normalizeRole(member.role) ?? null,
      })),
    [crew],
  );

  return { crew, crewById, crewForPicker, nonCrewIds };
}
